import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Grade, GradeStatus } from '../../models/grade.school.model';
import { gradeAdapter, GradeState } from '../reducers/grade.school.reducer';
import { selectRouteParams } from './router.selector';

export const getGradeState = createFeatureSelector<GradeState>('grades');
export const getIds = (state: GradeState) => state.ids;

export const {
  selectIds: getGradeIds,
  selectEntities: getGradeEntities,
  selectAll: getAllGrades,
  selectTotal: getTotalGrades,
} = gradeAdapter.getSelectors(getGradeState);

export const loading = createSelector(
  getGradeState,
  (gradeState: GradeState) => gradeState.loading,
);

export const inProgress = createSelector(
  getGradeState,
  (gradeState: GradeState) => gradeState.inProgress,
);

export const getGradeById = ({
  gradeId,
  groupId,
  academicYear,
}: {
  gradeId: string;
  groupId: string | null;
  academicYear: number | null | undefined;
}) =>
  createSelector(getAllGrades, (allItems) => {
    if (!allItems || !groupId || !academicYear) return null;
    return allItems.find((item) => {
      return (
        item.id === gradeId &&
        item.groupId === groupId &&
        item.gradeYear === academicYear
      );
    });
  });

export const getGradeByTeacherId = ({
  teacherId,
  groupId,
  academicYear,
}: {
  teacherId: string;
  groupId: string | null;
  academicYear: number | null;
}) =>
  createSelector(getAllGrades, (allItems) => {
    if (!allItems || !groupId || !academicYear) return null;
    return allItems.find((item) => {
      return (
        item.teacherId === teacherId &&
        item.groupId === groupId &&
        item.gradeYear === academicYear
      );
    });
  });

export const getGradesByGroup = (groupId: string | null, sort?: string) =>
  createSelector(getAllGrades, (allItems) => {
    if (!groupId || !allItems) return [];
    return allItems
      .filter((item) => {
        return (
          item.groupId === groupId && item.gradeStatus === GradeStatus.active
        );
      })
      .sort(sort == 'desc' ? sortDescendingByAge : sortAscendingByAge);
  });

export const getGradeIdsByGroup = (groupId: string) =>
  createSelector(getAllGrades, (allItems) => {
    return allItems
      .filter(
        (item) =>
          item.groupId === groupId && item.gradeStatus === GradeStatus.active,
      )
      .map((item) => item.id)
      .filter((id) => id !== undefined);
  });

export const getGradeCountForSelectedGroup = createSelector(
  getAllGrades,
  selectRouteParams,
  (allItems, params) => {
    const id = params?.['id'];
    if (!id) return 0;
    return allItems.filter((item) => {
      return item.groupId === id && item.gradeStatus === GradeStatus.active;
    }).length;
  },
);

function sortAscendingByAge(a: Grade, b: Grade): number {
  return a.gradeAvgAge > b.gradeAvgAge
    ? 1
    : a.gradeAvgAge < b.gradeAvgAge
      ? -1
      : 0;
}

function sortDescendingByAge(a: Grade, b: Grade): number {
  return a.gradeAvgAge < b.gradeAvgAge
    ? 1
    : a.gradeAvgAge > b.gradeAvgAge
      ? -1
      : 0;
}
